import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Alert,
  Chip,
  Card,
  CardContent
} from '@mui/material';
import { 
  CheckCircle, 
  Cancel, 
  ChangeCircle,
  Schedule,
  TrendingUp,
  PendingActions
} from '@mui/icons-material';
import { format, parseISO } from 'date-fns';
import { it } from 'date-fns/locale';
import { 
  CambiTurno, 
  StatoCambiTurno,
  AzioneCambiTurno,
  ApprovaRifiutaRichiestaCambiTurnoRequest
} from '../types/types';
import { cambiTurnoService } from '../api/services';
import { authService } from '../api/authService';

const GestioneCambiTurno: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [richiestePerApprovazione, setRichiestePerApprovazione] = useState<CambiTurno[]>([]);
  
  const user = authService.getCurrentUser();

  const loadRichiestePerApprovazione = async () => {
    setLoading(true);
    try {
      const richieste = await cambiTurnoService.getRichiestePerApprovazione();
      setRichiestePerApprovazione(richieste);
    } catch (err) {
      setError('Errore nel caricamento delle richieste');
      console.error('Errore caricamento richieste:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRichiestePerApprovazione();
  }, []);

  const handleApprovaRifiuta = async (idCambiTurno: number, azione: AzioneCambiTurno) => {
    setLoading(true);
    setError(null);
    setSuccess(null);
    
    try {
      const request: ApprovaRifiutaRichiestaCambiTurnoRequest = {
        idCambiTurno,
        azione
      };

      await cambiTurnoService.approvaORifiutaRichiesta(request);
      setSuccess(`Richiesta ${azione === 'APPROVA' ? 'approvata' : 'rifiutata'} con successo`);
      await loadRichiestePerApprovazione();
    } catch (err) {
      setError(`Errore nell'${azione === 'APPROVA' ? 'approvazione' : 'rifiuto'} della richiesta`);
      console.error('Errore approvazione/rifiuto:', err);
    } finally {
      setLoading(false);
    }
  };

  const getStatoChip = (stato: StatoCambiTurno) => {
    const colors = {
      'IN_ATTESA': 'warning',
      'IN_REVISIONE': 'info',
      'ACCETTATA': 'success',
      'RIFIUTATA': 'error',
      'ANNULLATA': 'default'
    } as const;

    return (
      <Chip
        label={stato.replace('_', ' ')}
        color={colors[stato]}
        size="small"
      />
    );
  };

  const formatDate = (dateString: string) => {
    try {
      return format(parseISO(dateString), 'dd/MM/yyyy', { locale: it });
    } catch {
      return dateString;
    }
  };

  const formatTime = (timeString?: string) => {
    if (!timeString) return '-';
    return timeString.substring(0, 5);
  };

  // Statistiche veloci
  const richiesteInRevisione = richiestePerApprovazione.filter(r => r.stato === 'IN_REVISIONE');
  const richiesteOggi = richiestePerApprovazione.filter(r => {
    const oggi = new Date().toISOString().split('T')[0];
    return r.dataCreazioneRichiesta.split('T')[0] === oggi;
  });

  if (!user || (user.ruolo !== 'Coordinatore' && user.ruolo !== 'Admin')) {
    return (
      <Container>
        <Alert severity="error">
          Non hai i permessi per accedere a questa sezione
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 3, textAlign: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
          <ChangeCircle fontSize="large" sx={{ mr: 2, color: '#FF5722' }} />
          <Typography variant="h4" component="h1">
            Gestione Cambi Turno
          </Typography>
        </Box>
        <Typography variant="subtitle1" color="textSecondary">
          Approva o rifiuta le richieste di cambio turno del tuo {user.ruolo === 'Admin' ? 'network' : 'negozio'}
        </Typography>
      </Box>

      {/* Statistiche rapide */}
      <Box display="flex" gap={2} sx={{ mb: 4, flexWrap: 'wrap' }}>
        <Card sx={{ flex: 1, minWidth: 280 }}>
          <CardContent>
            <Box display="flex" alignItems="center">
              <PendingActions sx={{ fontSize: 40, color: '#FF9800', mr: 2 }} />
              <Box>
                <Typography variant="h4" color="#FF9800">
                  {richiesteInRevisione.length}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  In Revisione
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
        
        <Card sx={{ flex: 1, minWidth: 280 }}>
          <CardContent>
            <Box display="flex" alignItems="center">
              <Schedule sx={{ fontSize: 40, color: '#2196F3', mr: 2 }} />
              <Box>
                <Typography variant="h4" color="#2196F3">
                  {richiesteOggi.length}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Richieste Oggi
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
        
        <Card sx={{ flex: 1, minWidth: 280 }}>
          <CardContent>
            <Box display="flex" alignItems="center">
              <TrendingUp sx={{ fontSize: 40, color: '#4CAF50', mr: 2 }} />
              <Box>
                <Typography variant="h4" color="#4CAF50">
                  {richiestePerApprovazione.length}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Totale Richieste
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Messaggi di feedback */}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      {/* Tabella delle richieste */}
      <Paper>
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Richieste di Cambio Turno da Approvare
          </Typography>
        </Box>
        
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell><strong>Data Richiesta</strong></TableCell>
                <TableCell><strong>Richiedente</strong></TableCell>
                <TableCell><strong>Accettante</strong></TableCell>
                <TableCell><strong>Data Turno</strong></TableCell>
                <TableCell><strong>Scambio Turni</strong></TableCell>
                <TableCell><strong>Orari</strong></TableCell>
                <TableCell><strong>Negozio</strong></TableCell>
                <TableCell><strong>Stato</strong></TableCell>
                <TableCell><strong>Motivazione</strong></TableCell>
                <TableCell align="center"><strong>Azioni</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={10} align="center">
                    <Typography>Caricamento...</Typography>
                  </TableCell>
                </TableRow>
              ) : richiestePerApprovazione.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center">
                    <Typography color="textSecondary">
                      Nessuna richiesta da approvare
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                richiestePerApprovazione.map((richiesta) => (
                  <TableRow 
                    key={richiesta.idCambiTurno}
                    sx={{ 
                      '&:hover': { backgroundColor: '#f9f9f9' },
                      backgroundColor: richiesta.stato === 'IN_REVISIONE' ? '#fff3e0' : 'inherit'
                    }}
                  >
                    <TableCell>{formatDate(richiesta.dataCreazioneRichiesta)}</TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {richiesta.turnoRichiedente.descrizione}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {richiesta.turnoAccettante?.descrizione || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>{formatDate(richiesta.turnoRichiedente.data)}</TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2">
                          <strong>{richiesta.turnoRichiedente.turno}</strong> ↔ <strong>{richiesta.turnoAccettante?.turno}</strong>
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="caption" display="block">
                          {formatTime(richiesta.turnoRichiedente.oraPrevInizio)} - {formatTime(richiesta.turnoRichiedente.oraPrevFine)}
                        </Typography>
                        <Typography variant="caption" display="block">
                          {formatTime(richiesta.turnoAccettante?.oraPrevInizio)} - {formatTime(richiesta.turnoAccettante?.oraPrevFine)}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={richiesta.turnoRichiedente.neg} 
                        size="small" 
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>{getStatoChip(richiesta.stato)}</TableCell>
                    <TableCell>
                      <Tooltip title={richiesta.motivazione} arrow>
                        <Typography 
                          variant="body2" 
                          noWrap 
                          sx={{ 
                            maxWidth: 150,
                            cursor: 'help'
                          }}
                        >
                          {richiesta.motivazione}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="center">
                      {richiesta.stato === 'IN_REVISIONE' ? (
                        <Box display="flex" gap={1} justifyContent="center">
                          <Tooltip title="Approva">
                            <IconButton
                              color="success"
                              onClick={() => handleApprovaRifiuta(richiesta.idCambiTurno, 'APPROVA')}
                              disabled={loading}
                              size="small"
                            >
                              <CheckCircle />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Rifiuta">
                            <IconButton
                              color="error"
                              onClick={() => handleApprovaRifiuta(richiesta.idCambiTurno, 'RIFIUTA')}
                              disabled={loading}
                              size="small"
                            >
                              <Cancel />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      ) : richiesta.stato === 'IN_ATTESA' ? (
                        <Typography variant="caption" color="textSecondary">
                          In attesa di accettazione
                        </Typography>
                      ) : (
                        <Typography variant="caption" color="textSecondary">
                          Già gestita
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Container>
  );
};

export default GestioneCambiTurno;
